<script setup lang="ts">
/**
 * SpreadQuickDraw — a quick card draw on the SEO spread page itself (SEO pilot,
 * docs/seo/competitors-spreads-2026-10.md §2.2: the competitors give the result on the
 * ranking URL; the personal reading stays the next step in the app).
 *
 * SSR / prerender: only the face-down stickers (one per card, max 6), their position tags
 * and the draw button — static, index-seeded tilts, nothing random or DOM-dependent, so
 * the server HTML and the first client render are identical.
 * On click (client only): the card data is read through import() (today it is already in the
 * main chunk — the router imports the card screens statically — so this resolves at once), N distinct
 * cards are drawn, their faces decoded, then the stickers turn over one by one (the
 * TarotCard flip — transform only, instant under reduced motion) and the short meaning
 * of each card fades in under them, with a link to its /karty page.
 */
import { computed, nextTick, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { Button } from '@/components/ui/button';
import { usePostHog } from '@core/analytics';
import { CARD_BACK_SRC } from '@app/data/spreadCutouts';
import { TURN_MS, motionSleep, prefersReducedMotion } from '@app/composables/useHangingFx';
import TarotCard from '@app/presentation/components/tarot/TarotCard.vue';

const props = defineProps<{
  /** SPREAD_SEO_DATA slug — the analytics key and the «карта дня» wording. */
  spreadSlug: string;
  cardCount: number;
  /** Where the personal reading starts (the hero CTA's target). */
  readingRoute: string;
  /** Position names in draw order (the pilot example's); missing ones → «Карта N». */
  positions?: string[];
}>();

const MAX_CARDS = 6;
/** Cards turn over on an even beat, each starting this long after the previous one. */
const BEAT_MS = 170;
/** Don't hold the reveal hostage to a slow image: turn over after this at the latest. */
const DECODE_WAIT_MS = 1500;

const count = Math.max(1, Math.min(MAX_CARDS, props.cardCount));
const isDaily = props.spreadSlug === 'karta-dnya';

const positionNames = Array.from({ length: count }, (_, i) => props.positions?.[i] ?? `Карта ${i + 1}`);

// Index-seeded (SSR-stable) tilts for the stickers and their tags.
const CARD_TILTS = [-2.2, 1.6, -1, 2, -1.6, 1.1];
const TAG_TILTS = [1.2, -1, 0.8, -1.3, 1, -0.7];

// Column widths per count, as literal classes (Tailwind scans them): one row up to 4 cards
// on a 375px phone, 5–6 cards → 3 + 3 (one row from md up). minmax(0, …) lets the columns
// shrink on 320px phones instead of spilling past the gutter.
const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-[128px] sm:grid-cols-[148px]',
  2: 'grid-cols-[repeat(2,112px)] sm:grid-cols-[repeat(2,128px)]',
  3: 'grid-cols-[repeat(3,minmax(0,92px))] sm:grid-cols-[repeat(3,120px)]',
  4: 'grid-cols-[repeat(4,minmax(0,70px))] sm:grid-cols-[repeat(4,108px)]',
  5: 'grid-cols-[repeat(3,minmax(0,88px))] md:grid-cols-[repeat(5,104px)]',
  6: 'grid-cols-[repeat(3,minmax(0,88px))] md:grid-cols-[repeat(6,112px)]',
};
const gridCols = GRID_COLS[count];

const title = isDaily ? 'Вытяни карту дня онлайн' : 'Вытяни карты онлайн';
const lead = isDaily
  ? 'Подумай о своём дне и нажми на кнопку — карта откроется прямо здесь, с коротким значением.'
  : 'Задумай вопрос и нажми на кнопку — карты откроются прямо здесь: у каждой позиция и короткое значение.';
const drawLabel = isDaily ? 'Вытянуть карту дня' : 'Вытянуть карты';

interface DrawnCard {
  slug: string;
  name: string;
  meaning: string;
  face: string;
}

type Phase = 'idle' | 'loading' | 'turning' | 'shown';
const phase = ref<Phase>('idle');
const drawn = ref<DrawnCard[]>([]);
/** The result text is visible (fades in as the cards land, out while they turn back). */
const textShown = ref(false);
const failed = ref(false);
/** Which stickers are face-up right now (mirrors each TarotCard's internal flip state). */
const faceUp: boolean[] = Array.from({ length: count }, () => false);

const busy = computed(() => phase.value === 'loading' || phase.value === 'turning');

const cardRefs: Array<InstanceType<typeof TarotCard> | null> = [];
const gridEl = ref<HTMLElement | null>(null);
const resultHeading = ref<HTMLElement | null>(null);

/** The card's gist: the first two sentences of its /karty tl;dr (else of the upright meaning). */
const shortMeaning = (tldr: string, meaning: string): string => {
  const text = tldr || meaning;
  // No lookbehind (old Safari targets): match whole sentences instead of splitting.
  const sentences = text.match(/[^.!?…]+[.!?…]+/g);
  return (sentences ? sentences.slice(0, 2).join('') : text).trim();
};

/** N distinct random cards (partial Fisher–Yates over the deck). Client only. */
const pick = (deck: string[], n: number): string[] => {
  const pool = deck.slice();
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(Math.random() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n);
};

/** Decode the faces before they turn over (a lazily decoded face hitches mid-flip). */
const decodeFaces = (urls: string[]): Promise<unknown> =>
  Promise.race([
    Promise.all(
      urls.map(url => {
        const img = new Image();
        img.src = url;
        return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
      }),
    ),
    new Promise(resolve => setTimeout(resolve, DECODE_WAIT_MS)),
  ]);

const turn = async (up: boolean, stagger: number) => {
  const reduced = prefersReducedMotion();
  for (let i = 0; i < count; i++) {
    if (faceUp[i] === up) continue;
    if (i > 0 && stagger && !reduced) await motionSleep(stagger);
    cardRefs[i]?.flip();
    faceUp[i] = up;
  }
};

const capture = (event: 'seo_quick_draw' | 'seo_quick_draw_cta', extra: Record<string, unknown> = {}) => {
  usePostHog().posthog.capture(event, { spread_slug: props.spreadSlug, card_count: count, ...extra });
};

/**
 * Async on purpose: cardSeoData is in the main chunk today (statically imported by the card
 * screens), so this resolves immediately; if the marketing routes are code-split later it
 * becomes a real lazy load, with the hover prefetch and the failure message below.
 */
let cardData: Promise<typeof import('@app/data/cardSeoData')> | null = null;
const loadCardData = () => {
  cardData ??= import('@app/data/cardSeoData').catch(error => {
    cardData = null;
    throw error;
  });
  return cardData;
};

const draw = async () => {
  if (busy.value) return;
  const again = phase.value === 'shown';
  failed.value = false;
  phase.value = 'loading';

  let mod: Awaited<ReturnType<typeof loadCardData>>;
  try {
    mod = await loadCardData();
  } catch {
    failed.value = true;
    phase.value = again ? 'shown' : 'idle';
    return;
  }

  capture('seo_quick_draw', { is_redraw: again });

  const deck = [...mod.MAJOR_ARCANA_SLUGS, ...mod.MINOR_ARCANA_SLUGS];
  const next = pick(deck, count).map(slug => {
    const card = mod.CARD_SEO_DATA[slug];
    return {
      slug,
      name: card?.name ?? slug,
      meaning: card ? shortMeaning(card.tldr, card.upright.meaning) : '',
      face: mod.cardImageSrc(slug).webp,
    };
  });
  const decoded = decodeFaces(next.map(c => c.face));

  phase.value = 'turning';
  if (again) {
    // Bring the cards back into view, fade the old text and turn the cards face down.
    gridEl.value?.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    textShown.value = false;
    await turn(false, 0);
    await motionSleep(TURN_MS);
  }

  // The result block goes in now (final layout right after the click), its text still hidden.
  drawn.value = next;
  await nextTick();
  if (!again) resultHeading.value?.focus({ preventScroll: true });
  await decoded;

  // Turn the cards over one by one; the text fades in as they land (CSS delays per item).
  textShown.value = true;
  await turn(true, BEAT_MS);
  await motionSleep(TURN_MS);
  phase.value = 'shown';
};

const onCta = () => capture('seo_quick_draw_cta');
</script>

<template>
  <section
    class="pb-14 md:pb-20"
    aria-labelledby="quick-draw-title"
  >
    <div class="site-container">
      <div class="mx-auto max-w-[860px]">
        <div class="type-overline mb-3.5 text-primary-ink">
          Прямо на странице
        </div>
        <h2
          id="quick-draw-title"
          class="type-title"
        >
          {{ title }}
        </h2>
        <p class="type-body mt-4 max-w-[640px] text-muted-foreground md:text-[17px]">{{ lead }}</p>

        <!-- The cards, face down until the draw: stickers with their position tags. -->
        <ul
          ref="gridEl"
          class="qd-grid mt-9 grid scroll-mt-24 justify-center gap-x-4 gap-y-7 sm:gap-x-6"
          :class="[gridCols, { 'is-idle': phase === 'idle', 'is-dense': count >= 4 }]"
        >
          <li
            v-for="(position, i) in positionNames"
            :key="i"
            class="flex flex-col items-center gap-3.5"
          >
            <div
              class="qd-card relative w-full"
              :style="{ rotate: `${CARD_TILTS[i % CARD_TILTS.length]}deg` }"
              @click="phase === 'idle' && draw()"
            >
              <TarotCard
                :ref="el => (cardRefs[i] = el as InstanceType<typeof TarotCard> | null)"
                :card-key="`quick-${i}`"
                :image-url="drawn[i]?.face ?? CARD_BACK_SRC"
                :back-image-url="CARD_BACK_SRC"
                :label="drawn[i]?.name"
                :flip-duration="`${TURN_MS}ms`"
                loading="lazy"
              />
            </div>
            <span
              class="qd-pos seo-tag paper-tag px-2 py-0.5 text-center"
              :style="{ '--tilt': `${TAG_TILTS[i % TAG_TILTS.length]}deg` }"
            >{{ position }}</span>
          </li>
        </ul>

        <div
          v-if="!drawn.length"
          class="mt-9 flex flex-col items-center gap-3"
        >
          <Button
            size="lg"
            :disabled="busy"
            :aria-busy="busy"
            @pointerenter="loadCardData().catch(() => {})"
            @focus="loadCardData().catch(() => {})"
            @click="draw"
          >
            {{ busy ? 'Тасую колоду…' : drawLabel }}
          </Button>
          <p
            v-if="failed"
            role="alert"
            class="type-caption text-center"
          >
            Не получилось вытянуть карты — проверь интернет и попробуй ещё раз.
          </p>
        </div>

        <!-- The result: a pencil-ruled sheet (as the worked example), then the next step. -->
        <div
          v-else
          class="qd-result mt-10"
          :class="{ 'is-shown': textShown }"
        >
          <h3
            ref="resultHeading"
            tabindex="-1"
            class="sr-only"
          >
            {{ count === 1 ? 'Твоя карта' : 'Твои карты' }}
          </h3>
          <ol
            class="qd-sheet seo-lines paper-sheet px-5 md:px-7"
            :style="{ '--qd-delay': `${TURN_MS * 0.55}ms` }"
            :aria-busy="busy"
          >
            <li
              v-for="(card, i) in drawn"
              :key="`${i}-${card.slug}`"
              class="qd-item flex gap-4 py-5 md:py-6"
              :style="{ '--qd-delay': `${i * BEAT_MS + TURN_MS * 0.55}ms` }"
            >
              <span
                v-if="count > 1"
                class="seo-disc type-label h-8 w-8"
                aria-hidden="true"
              >{{ i + 1 }}</span>
              <div class="min-w-0">
                <h4 class="type-app-heading text-foreground">
                  {{ positionNames[i] }} —
                  <RouterLink
                    :to="`/karty/${card.slug}`"
                    class="seo-link"
                  >{{ card.name }}</RouterLink>
                </h4>
                <p class="type-body mt-2 text-muted-foreground">{{ card.meaning }}</p>
              </div>
            </li>
          </ol>

          <div
            class="qd-next mt-8"
            :style="{ '--qd-delay': `${(count - 1) * BEAT_MS + TURN_MS * 0.8}ms` }"
          >
            <p class="type-body max-w-[640px] text-muted-foreground">
              Это общее значение {{ count === 1 ? 'карты' : 'каждой карты' }}. Личный разбор Таро Кот делает в
              приложении — под твой вопрос{{ count === 1 ? '' : ', по позициям и в связке всех карт' }}.
            </p>
            <div class="mt-5 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Button
                as-child
                size="lg"
              >
                <RouterLink
                  :to="readingRoute"
                  @click="onCta"
                >
                  Получить личный разбор от кота
                </RouterLink>
              </Button>
              <!-- aria-disabled, not disabled: a disabled button would drop the keyboard focus. -->
              <Button
                v-if="!isDaily"
                variant="outline"
                size="lg"
                :aria-disabled="busy"
                @click="draw"
              >
                Вытянуть ещё раз
              </Button>
            </div>
            <p
              v-if="failed"
              role="alert"
              class="type-caption mt-3"
            >
              Не получилось вытянуть карты — проверь интернет и попробуй ещё раз.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped src="./seo-paper.css"></style>

<style scoped>
/* Bigger stickers want a slightly wider die-cut ring. */
.qd-card {
  --sticker-w: 3px;
}
@media (min-width: 640px) {
  .qd-card {
    --sticker-w: 4px;
  }
}
/* Position tag under a card: small 600 sentence case, as the stage's position labels. */
.qd-pos {
  /* A tag may run a little into the column gap; long words hyphenate rather than overflow. */
  max-width: calc(100% + 0.75rem);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.25;
  hyphens: auto;
  overflow-wrap: break-word;
}
.qd-grid.is-dense .qd-pos {
  padding-inline: 0.375rem;
  font-size: 11px;
}
@media (min-width: 640px) {
  .qd-pos,
  .qd-grid.is-dense .qd-pos {
    font-size: 13px;
  }
}
/* Face down, the cards themselves draw; once drawn they're just cards (TarotCard sets a pointer). */
.qd-grid :deep(.card) {
  cursor: default;
}
.qd-grid.is-idle :deep(.card) {
  cursor: pointer;
}

/* The text fades in as each card lands (opacity + a short rise — no layout), out at once. */
.qd-sheet,
.qd-item,
.qd-next {
  opacity: 0;
  translate: 0 6px;
  transition:
    opacity 0.2s ease-out,
    translate 0.2s ease-out;
}
.qd-result.is-shown .qd-sheet,
.qd-result.is-shown .qd-item,
.qd-result.is-shown .qd-next {
  opacity: 1;
  translate: 0 0;
  transition-duration: 0.45s;
  transition-delay: var(--qd-delay, 0ms);
}

@media (prefers-reduced-motion: reduce) {
  .qd-sheet,
  .qd-item,
  .qd-next,
  .qd-result.is-shown .qd-sheet,
  .qd-result.is-shown .qd-item,
  .qd-result.is-shown .qd-next {
    translate: none;
    transition: none;
  }
}
</style>
